// Load-ordering rules for the Inventory page. Pure, so they can be unit-tested without
// mounting a Redux-wired table (see CLAUDE.md "test pure logic").

/**
 * Whether the vehicle list must hold its fetch until the fleet/depot scope has loaded.
 *
 * `globalFilters` starts as {} and is filled asynchronously — fetchFleets, then
 * fetchOnboardingLocations, in InventoryMain. Those values become the `fleetIDs` /
 * `originLocations` request headers (Overview/CommonFunctions.js), and those headers are
 * only attached when the values are non-empty.
 *
 * Without this gate the list fired TWICE per page load: once immediately with no scope
 * headers, then again when the scope arrived and changed the effect's dependency. The
 * duplicate request is the lesser problem — the first one is answered with rows from
 * outside the user's fleet/depot scope, and they render until the second returns.
 *
 * Deliberately false when the org has no global filter, since fetchFleets is never called
 * there and the flag would never flip.
 *
 * `canUseGlobalAdvancedFilter` comes from useCanUseGlobalAdvancedFilter
 * (Helper/Permissions/GlobalAdvancedFilter.js), which requires RBAC v2. So v1 orgs, and
 * v2 orgs lacking the feature code, take the ungated path: no RBAC fleet list is fetched
 * and the list sends no scope headers, which the backend scopes on its own. Only a v2
 * org with the feature code waits here.
 *
 * The real guarantee therefore has to live at the setter: InventoryMain must resolve
 * `globalFilterReady` on every terminal path -- empty fleet list, failed fleet fetch,
 * failed location fetch -- or the list never loads at all for those users. It previously
 * resolved only on the happy path, which stranded anyone with no fleet assignments on a
 * permanent spinner.
 */
export const shouldWaitForGlobalFilter = (
    canUseGlobalAdvancedFilter,
    globalFilterReady
) => !!canUseGlobalAdvancedFilter && !globalFilterReady;

/**
 * The count fetch belonging to a tab, by the positional index the BasicTabPanels use:
 * 0 All Vehicles, 1 Battery Configurations, 2 All Battery Packs, 3 Unmapped IOT Devices.
 *
 * Each count feeds the chip beside that tab's OWN table title, so only the visible tab's
 * number is ever on screen. Running all four on mount cost three requests for tabs the
 * user may never open — including ones hidden by tabsToShow or RBAC.
 *
 * Returns null for an unknown index rather than throwing: the tab list is filtered by
 * config and permissions, so an index with no count attached is a normal state.
 */
export const TAB_COUNT_KEYS = ["connectedBatteries", "configs", "batteryPacks", "unlinkedDevices"];

export const getTabCountKey = (tabValue) => TAB_COUNT_KEYS[tabValue] ?? null;

/**
 * Whether this run of the fetch effect should stand down because a page reset is about
 * to land and will fetch again anyway.
 *
 * The list resets to page 1 whenever filters, search, sort or the global filter change,
 * but that reset is dispatched from its OWN effect, declared after the fetch effect. So
 * in the commit where one of those changes, the fetch effect runs FIRST — with the new
 * criteria and the OLD page number — and then the reset lands and fires it again.
 *
 * Two requests, and the first one asks for the wrong page: on page 3 of the old filter
 * set it fetches page 3 of the new one, which briefly renders rows the user did not ask
 * for and may be empty entirely.
 *
 * Skipping is safe precisely because the reset effect watches the same four values, so
 * whenever this returns true a dispatch is already queued in the same commit. The caller
 * must NOT record these params as fetched when it stands down, or the follow-up run will
 * look unchanged and fetch nothing at all.
 *
 * Returns false on the first run (no previous params), where nothing has changed yet and
 * the page is whatever the user arrived on.
 *
 * @param {object|null} previous - the last params actually fetched
 * @param {object} current - the params this run would fetch
 * @param {number} pageNumber - the page this run would ask for
 */
export const RESET_TRIGGERING_KEYS = [
    "selectedFilters",
    "searchTerm",
    "sorting",
    "globalFilters",
];

export const shouldDeferToPageReset = (previous, current, pageNumber) => {
    if (!previous || pageNumber === 1) return false;
    return RESET_TRIGGERING_KEYS.some((key) => previous[key] !== current[key]);
};
